import { useInterval, useUnmount, useUpdateEffect } from 'ahooks';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { useCaseInsensitiveParams, useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '@/pages/aigc/hooks/useContext/useStore';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { useEffect, useCallback, useRef, useMemo } from 'react';
import { findLast, isEmpty, last } from 'lodash-es';
import { useAgentStoreContext } from '../../ChatPage/hooks/useLocalState';
import { useSessionId } from '../../ChatPage/hooks/useAgentModalSata';
import { parseJsonSafely } from '../../ChatPage/utils';
import { resolveCosFileExtension } from '../../ChatPage/components/SubComp/_utils';

interface UseBeforeUnloadOptions {
  /**
   * 是否启用hook
   */
  enabled?: boolean;
  /**
   * 自定义提示消息
   */
  message?: string;
}

/**
 * 监听页面刷新/关闭的Hook
 * @param callback 刷新前的回调函数，返回true则允许刷新，返回false或void则阻止刷新
 * @param options 配置选项
 */
export function useBeforeUnload(
  callback: (event?: BeforeUnloadEvent) => boolean | void,
  options: UseBeforeUnloadOptions = {},
) {
  const { enabled = true, message } = options;
  const callbackRef = useRef(callback);
  const isEnabledRef = useRef(enabled);

  // 更新回调引用
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // 更新启用状态引用
  useEffect(() => {
    isEnabledRef.current = enabled;
  }, [enabled]);

  // 处理beforeunload事件
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isEnabledRef.current) return;

      // 执行回调
      const shouldPrevent = callbackRef.current(event);

      // 如果回调返回false或undefined，或者需要显示提示消息
      if (shouldPrevent === false || shouldPrevent === undefined) {
        // 标准方式
        event.preventDefault();

        // 为了兼容性，需要设置returnValue (ESLint Fix #2)
        // 注意：这是 DOM API 的要求，无法直接避免修改参数属性
        // eslint-disable-next-line no-param-reassign
        if (message) {
          event.returnValue = message;
        } else {
          event.returnValue = '您确定要离开吗？未保存的更改可能会丢失。';
        }

        return event.returnValue;
      }
    },
    [message],
  );

  // 添加和移除事件监听器
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, handleBeforeUnload]);
}

/**
 * 监听页面可见性变化的Hook
 * @param callback 页面变为不可见时的回调函数
 */
export function usePageVisibility(callback: () => void) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        callbackRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}

/**
 * 完整的页面刷新监听Hook
 * @param onRefresh 刷新时的回调函数
 * @param options 配置选项
 */
export function usePageRefresh(
  onRefresh: () => void,
  options: UseBeforeUnloadOptions & {
    /**
     * 是否监听页面可见性变化（标签页切换）
     */
    watchVisibility?: boolean;
  } = {},
) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { watchVisibility = false, ...beforeUnloadOptions } = options;

  useBeforeUnload(() => {
    onRefresh();
    // 返回false阻止默认刷新提示，返回true允许刷新
    return !beforeUnloadOptions.message;
  }, beforeUnloadOptions);
}

// 监听页面刷新，触发一次回调，
// 传入状态，页面状态变更，触发一次回调，
// 监听页面销毁了，触发一次回调，

interface IUsePageLifecycleProps {
  deps?: any[];
  callback: () => void;
  isDiffRender?: any;
  value?: any;
}

export function usePageLifecycle(props: IUsePageLifecycleProps) {
  const { deps, callback, isDiffRender, value } = props || {};
  const handleRefresh = () => {
    console.log('页面即将刷新，保存数据...');
    callback();
  };

  usePageRefresh(handleRefresh, {
    message: '确定要离开吗？未保存的数据将会丢失。',
  });

  useUnmount(() => {
    handlePrompt();
  });
  
  useUpdateEffect(() => {
    deps && handlePrompt();
  }, [deps]);

  useInterval(() => {
    handlePrompt();
  }, 30000);

  const handlePrompt = () => {
    isDiffRender?.getMarkdown() !== value && value && callback?.();
  };
}

export const useChatGetAgentDocument = () => {
  const showErrorTips = useErrorTips();
  const data = useRequestController({
    method: 'POST',
    url: `/api/agent_com/get_agent_document`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onSuccess: (response) => {
      showErrorTips?.(response?.res_code);
    },
  });
  return data;
};

export const useChatListAgentDocuments = () => {
  const showErrorTips = useErrorTips();
  const data = useRequestController({
    method: 'POST',
    url: `/api/agent_com/list_agent_documents`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onError: (err: any) => {
      console.log(err);
    },
    onSuccess: (response) => {
      showErrorTips?.(response?.res_code);
    },
  });
  return data;
};

export const useChatSaveAgentDocument = () => {
  const showErrorTips = useErrorTips();
  const data = useRequestController({
    method: 'POST',
    url: `/api/agent_com/save_agent_document`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onSuccess: (response) => {
      showErrorTips?.(response?.res_code);
    },
    // 离开是否触发请求
    leaveRequest: true,
  });
  return data;
};

export const useChatResetAgentDocument = () => {
  const showErrorTips = useErrorTips();
  const data = useRequestController({
    method: 'POST',
    url: `/api/agent_com/reset_agent_document`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onSuccess: (response) => {
      showErrorTips?.(response?.res_code);
    },
  });
  return data;
};

export const useDocumentOperations = ({
  stepActive,
  callBack,
  sessionIdForDocument,
}: {
  stepActive: string;
  callBack: (...args: any[]) => void;
  /** 报告相关接口使用的会话 ID，不传则回退到当前路由/分享 ID 推导的 sessionId */
  sessionIdForDocument?: string;
}) => {
  const { runAsync: saveReportAsync } = useChatSaveAgentDocument();
  const { runAsync: getReportAsync, loading, data: reportDataDetail  } = useChatGetAgentDocument();
  const { runAsync: resetReportAsync, loading: resetLoading } = useChatResetAgentDocument();
  const { runAsync: getFileListAsync, data: reportFileList, loading: listLoading } =
    useChatListAgentDocuments();
  const { sessionId: urlSessionId } = useSessionId();
  const { poolId, shareId, msgid } = useCaseInsensitiveParams(useSearchParamsAppoint(), ["shareId", 'poolId', 'msgid']);
  const sessionId = sessionIdForDocument || urlSessionId || shareId || poolId || msgid;
  const [chartsConfig, setChartsConfig] = useStore<{
    [key in string]: any;
  }>('chartsAiSetting', {
    // // 当前筛选值
    // newFilterInfoVal: null,
    // // 当前指标
    // currentMetric: null,
  });

  // 获取文档内容
  const fetchDocument = async (
    document_id: string,
    version?: string,
    initialType?: string,
  ) => {
    if (!document_id || !sessionId) {
      return null;
    }

    const res = await getReportAsync({
      document_id,
      version,
      session_id: sessionId,
      trace_id: uuidv4(),
      document_type: initialType || stepActive,
    });
    // setChartsConfig({
    //   ...chartsConfig,
    //   currentMetric: res?.cursor_position?.currentMetric,
    //   newFilterInfoVal: res?.cursor_position?.newFilterInfoVal,
    // });
    setChartsConfig(res?.cursor_position || {});
    callBack(res);
    return res;
  };

  // 保存文档
  const saveDocument = async (text: string, documentInfo: any) =>
    saveReportAsync({
      text,
      bi_data: reportDataDetail?.bi_data || '',
      ...documentInfo,
      trace_id: uuidv4(),
      session_id: sessionId,
    });

  // 重置文档
  const resetDocument = async (document_id: string, document_type: string, version?: string) =>
    resetReportAsync({
      trace_id: uuidv4(),
      session_id: sessionId,
      document_id,
      document_type,
      version
    });

  // 获取文档列表
  const getFileList = async ({ document_type }: { document_type: string; }) =>
    getFileListAsync({
      trace_id: uuidv4(),
      session_id: sessionId,
      document_type,
    });

  return {
    fetchDocument,
    saveDocument,
    resetDocument,
    getFileList,
    chartsConfig,
    setChartsConfig,
    loading,
    reportFileList,
    listLoading,
    resetLoading,
    reportDataDetail,
  };
};

export interface UseAutoLayoutOptions {
  conversations: any[];
  streamingState: string | boolean | null;
  dsReport?: {
    initialShow?: boolean;
    reportId?: string;
    show?: boolean;
    componentType?: string;
  };
  onOpenDsReport?: (params: {
    show: boolean;
    initialShow: boolean;
    answer: any;
    messageId: string;
    reportId: string;
  }) => void;
  /** 自定义打开报告的处理函数（用于 Copilot 等需要特殊打开逻辑的场景） */
  customOpenHandler?: (params: {
    lastDocument: any;
    lastDocumentSubType: string | null;
    lastItem: any;
    messageId: string;
    targetId: string;
    params: any; // 格式化后的完整参数，包含 show、initialShow、componentType 等
  }) => void;
  value?: any;
  answer?: any;
}

export function useAutoLayout({
  conversations,
  streamingState,
  dsReport,
  onOpenDsReport,
  customOpenHandler,
}: UseAutoLayoutOptions) {
  const { sessionId: sessionId } = useSessionId();
  // 修正左侧菜单是否打开
  const { getItem, setItem } = useAgentStoreContext();
  const isShowMenu = getItem(`__dba_chat_ds_report__${sessionId}`);
  // 安全的 dsReport 默认值，避免 TypeScript 严格空检查错误
  const safeDsReport = dsReport || { show: false, initialShow: false };
  
  // 会话切换时重置 initialShow 为 false，与 DS 报告逻辑保持一致
  // DS 报告跳转其他对话再回来后默认关闭，AI 表格和数据魔法海报也应如此
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      // 会话已切换
      prevSessionIdRef.current = sessionId;
      // 如果当前报告是显示状态，关闭它
      if (safeDsReport.show) {
        onOpenDsReport?.({
          show: false,
          initialShow: false,
          answer: (dsReport as any)?.answer,
          messageId: (dsReport as any)?.messageId || '',
          reportId: (dsReport as any)?.reportId || '',
        });
      }
    }
  }, [sessionId]);

  // 从 conversations 最后一条消息中提取 lastDocument
  const lastConversationItem = last(conversations);
  const lastAnswerLen = lastConversationItem?.answer?.length || 0;
  const lastMessageId = lastConversationItem?.messageId || null;

  // 生成报告、计划，一定是最后一个item
  // 统一查找 type === 'document' 的项（document/ai_excel_file/magic_poster_file 都统一为 document）
  const {lastDocument, lastDocumentSubType, lastItem, messageId} = useMemo(() => {
    if (isEmpty(conversations) && !safeDsReport.show) return {lastDocument: null, lastDocumentSubType: null, lastItem: null, messageId: null};
    
    const docItem = findLast(lastConversationItem?.answer, (item) => item.type === 'document');
    if (docItem) {
      return {
        lastDocument: docItem?.value,
        lastDocumentSubType: docItem?.subType || 'document',
        lastItem: lastConversationItem,
        messageId: lastMessageId,
      };
    }
    // 检测 text 中的 <cos> 标签（ai_gallery_file / ai_excel_file 场景）
    // 与 document/ai_excel_file/magic_poster_file 保持一致的自动打开逻辑，不再限制 skill/splitView 模板，
    // 确保任意会话模式下生成的 AI Gallery HTML / AI 表格 excel 都能自动打开（包括跳转 Copilot 后自动还原展示）
    const textItem = findLast(lastConversationItem?.answer, (item: any) => 
      item.type === 'text' && item.value?.includes('<cos>')
    );
    if (textItem) {
      const match = textItem.value.match(/<cos>([\s\S]*?)<\/cos>/);
      const cosData = parseJsonSafely(match?.[1]);
      // 与 DocumentCmp 一致：HTML（AI Gallery）与 excel（AI 表格）自动打开左右布局，其他文件（zip 等）不自动打开
      const fileExt = resolveCosFileExtension(
        cosData?.url || cosData?.cos_url || '',
        cosData?.name || cosData?.title || '',
      );
      const isExcelFile = fileExt === 'xlsx' || fileExt === 'xls';
      if (cosData && (fileExt === 'html' || isExcelFile)) {
        return {
          // cos 标签来源标记（excel 场景）：与 SubComp 手动点击一致，确保跳过 get_excel_info、Copilot 跳转保持 splitView 只读
          lastDocument: { ...cosData, id: lastMessageId, document_id: lastMessageId, isCosTag: isExcelFile },
          lastDocumentSubType: isExcelFile ? 'ai_excel_file' : 'ai_gallery_file',
          lastItem: lastConversationItem,
          messageId: lastMessageId,
        };
      }
    }
    
    return { lastDocument: null, lastDocumentSubType: null, lastItem: null, messageId: null };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAnswerLen, lastMessageId, safeDsReport.show,]);

  // 根据组件类型获取对应的文档/文件 ID
  // 优先使用 dsReport?.componentType（用户点击时传递的类型），自动打开时使用 lastDocumentSubType
  // document 类型使用 document_id，ai_excel_file/magic_poster_file 类型使用 id
  const currentComponentType = safeDsReport.componentType || lastDocumentSubType;
  const isDocument = currentComponentType === 'document';
  const targetId = isDocument
    ? lastDocument?.document_id
    : (lastDocument?.id || lastDocument?.document_id);

  // 记录上次 Effect3 已处理过的 targetId，避免用户手动打开时被 lastDocument 覆盖
  const prevTargetIdRef = useRef<string | undefined>(undefined);

  const handleOpenDsReport = () => {
    const params: Record<string, any> = {
      show: true,
      initialShow: true,
      ...lastDocument,
      // 优先使用 dsReport?.componentType（用户点击），否则使用 lastDocumentSubType（自动打开）
      componentType: currentComponentType,
      answer: lastItem?.answer,
      messageId,
      reportId: messageId,
      // 记录生成/绑定该报告时所使用的会话 ID，确保报告相关接口的 sessionId 稳定
      sourceSessionId: sessionId,
    };
    
    // 如果提供了 customOpenHandler，则使用它；否则，使用默认的 onOpenDsReport
    if (customOpenHandler) {
      customOpenHandler({
        lastDocument,
        lastDocumentSubType,
        lastItem,
        messageId,
        targetId,
        params, // 同时传递格式化后的 params，方便调用方使用
      });
    } else {
      onOpenDsReport?.(params as any);
    }
  };
  
  // 调整动机：初始化打开，后续不在打开
  useEffect(() => {
    if (
      streamingState &&
      targetId &&
      !isShowMenu &&
      window.innerWidth <= 1920
    ) {
      const onSet = getItem('__dba_show_left_menu__');
      onSet?.(true);
      setItem(`__dba_chat_ds_report__${sessionId}`, true);
    }
  }, [
    streamingState,
    targetId,
    isShowMenu,
    sessionId,
  ]);
  
  useEffect(() => {
    // 1、自动打开
    if (!safeDsReport.initialShow && streamingState && messageId && targetId) {
      handleOpenDsReport();
      // 同步记录当前 targetId，防止 Effect3 在首次打开时立刻覆盖
      prevTargetIdRef.current = targetId;
    }
  }, [streamingState, safeDsReport.initialShow, messageId, targetId]);

  useEffect(() => {
    // 打开不在计算
    if (safeDsReport.show) {
      return;
    }
    // 2、关闭的情况下，下次可以再次自动打开
    if (messageId && safeDsReport?.reportId !== messageId && streamingState && targetId) {
      handleOpenDsReport();
    }
  }, [messageId, safeDsReport?.reportId, streamingState, safeDsReport.show, targetId]);

  // 3、面板已打开时，如果出现新文件（lastDocument 变化），自动更新 dsReport 以触发面板刷新
  // 使用 prevTargetIdRef 判断 targetId 是否真正发生了变化（即 streaming 中出现了新文件），
  // 避免用户手动点击文档卡片打开面板后，被 lastDocument 立刻覆盖
  useEffect(() => {
    if (!safeDsReport?.show || !safeDsReport?.initialShow) return;
    if (!targetId || !messageId) return;

    // 首次进入时，记录当前 targetId 并跳过，不触发覆盖
    if (prevTargetIdRef.current === undefined) {
      prevTargetIdRef.current = targetId;
      return;
    }

    // 只有当 targetId 真正发生变化（streaming 产生了新文件）时才触发刷新
    if (prevTargetIdRef.current !== targetId) {
      prevTargetIdRef.current = targetId;
      handleOpenDsReport();
    }
  }, [safeDsReport?.show, safeDsReport?.initialShow, targetId, messageId]);

  return {
    lastDocument,
    lastDocumentSubType,
    messageId,
    targetId,
    currentComponentType,
  };
}
